import { DEFAULT_MEDIA_DURATION } from 'consts'
import { FIT } from 'consts/canvas'
import { IDimensions } from 'types'
import { IMediaItem, TMediaItemType, MediaStatus } from 'types/media'
import { FileRegistry } from 'utils/file-registry/FileRegistry'
import { calculateMediaRect } from 'utils/media'

interface IMediaParameters extends IDimensions {
  duration: number
}

const getImageParameters = async (url: string): Promise<IMediaParameters> => {
  return new Promise((resolve, reject) => {
    const fileRegistry = FileRegistry.getInstance()
    const img = new Image()
    img.onload = () => {
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
        duration: DEFAULT_MEDIA_DURATION
      })
    }
    img.onerror = () => {
      fileRegistry.deleteFile(url)
      reject('Failed to load image')
    }
    img.src = url
  })
}

const getVideoParameters = async (url: string): Promise<IMediaParameters> => {
  return new Promise((resolve, reject) => {
    const fileRegistry = FileRegistry.getInstance()
    const video = document.createElement('video')
    video.onloadedmetadata = () => {
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        duration: video.duration
      })
    }
    video.onerror = () => {
      fileRegistry.deleteFile(url)
      reject('Failed to load video')
    }
    video.src = url
  })
}

export const getMediaParameters = async (
  url: string,
  type: 'image' | 'video'
): Promise<IMediaParameters> => {
  return type === 'image'
    ? await getImageParameters(url)
    : await getVideoParameters(url)
}

const createMediaItem = async (
  file: File,
  canvasDimensions: IDimensions
): Promise<IMediaItem> => {
  const fileRegistry = FileRegistry.getInstance()
  const url = fileRegistry.registerFile(file)
  const isImage = file.type.startsWith('image/')
  const isVideo = file.type.startsWith('video/')
  const fileType = isImage ? 'image' : 'video'

  let mediaParameters
  let mediaStatus
  let mediaPath

  try {
    mediaParameters = await getMediaParameters(url, fileType)
    mediaPath = url
    mediaStatus = MediaStatus.OK
  } catch {
    mediaParameters = {
      width: 0,
      height: 0,
      duration: DEFAULT_MEDIA_DURATION
    }
    mediaStatus = MediaStatus.PENDING
    mediaPath = null
  }

  const mediaRect = calculateMediaRect({
    mediaDimensions: {
      width: mediaParameters.width,
      height: mediaParameters.height
    },
    mode: FIT,
    canvasDimensions,
    rotation: 0
  })

  return {
    id: crypto.randomUUID(),
    assetId: crypto.randomUUID(),
    type: fileType as TMediaItemType,
    width: mediaRect.width || 0,
    height: mediaRect.height || 0,
    duration: mediaParameters.duration,
    startTime: 0,
    zIndex: 0,
    rotation: 0,
    coordinates: {
      x: mediaRect.x || 0,
      y: mediaRect.y || 0
    },
    meta: {
      path: mediaPath,
      originalWidth: mediaParameters.width,
      originalHeight: mediaParameters.height,
      status: mediaStatus,
      ...(isVideo && {
        originalDuration: mediaParameters.duration,
        trimStart: 0,
        volume: 100
      })
    }
  }
}

export const assignStartTimes = (
  items: IMediaItem[],
  insertionStartTime: number
): IMediaItem[] => {
  return items.map((item, index) => ({
    ...item,
    startTime:
      insertionStartTime +
      items.slice(0, index).reduce((sum, prev) => sum + prev.duration, 0)
  }))
}

export const assignStartTimesAndZIndex = (
  items: IMediaItem[],
  startTime: number,
  zIndex: number
): IMediaItem[] =>
  assignStartTimes(items, startTime).map(item => ({ ...item, zIndex }))

export const createMediaItems = async (
  files: FileList | File[],
  canvasDimensions: IDimensions
): Promise<IMediaItem[]> => {
  return Promise.all(
    Array.from(files).map(file => createMediaItem(file, canvasDimensions))
  )
}

export const prepareMediaData = async (
  files: FileList | File[],
  insertionStartTime: number,
  canvasDimensions: IDimensions,
  zIndex: number = 0
): Promise<IMediaItem[]> => {
  const items = await createMediaItems(files, canvasDimensions)
  return assignStartTimesAndZIndex(items, insertionStartTime, zIndex)
}
